const HOME_ROUTE = '/home.html';

const normalizeContextPath = (contextPath: string): string => {
  if (!contextPath || contextPath === '/') {
    return '';
  }
  return contextPath.startsWith('/') ? contextPath : `/${contextPath}`;
};

const ensureContextPath = (path: string, contextPath: string): string => {
  const normalizedContextPath = normalizeContextPath(contextPath);
  if (!normalizedContextPath) {
    return path;
  }
  if (path.startsWith(`${normalizedContextPath}/`) || path === normalizedContextPath) {
    return path;
  }
  return `${normalizedContextPath}${path}`;
};

const isLegacyLoginPath = (path: string): boolean =>
  /\/go\/login(?:\/(?:index|auth))?\/?$/i.test(path);

const isLoginPath = (path: string): boolean =>
  isLegacyLoginPath(path)
  || path.endsWith('/login.html')
  || path.endsWith('/react/react-login.html')
  || path.endsWith('/assets/vite/react-login.html');

const fallbackRedirect = (contextPath: string): string =>
  ensureContextPath(HOME_ROUTE, contextPath);

const isContextRootPath = (pathname: string, contextPath: string): boolean => {
  const normalizedContextPath = normalizeContextPath(contextPath);
  if (!normalizedContextPath) {
    return pathname === '/';
  }
  return pathname === normalizedContextPath || pathname === `${normalizedContextPath}/`;
};

const isAllowedAbsoluteHost = (url: URL): boolean => {
  if (url.origin === window.location.origin) {
    return true;
  }

  const host = url.hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1';
};

const normalizeRedirectPath = (pathname: string, contextPath: string): string | null => {
  if (!pathname || pathname === '/' || isContextRootPath(pathname, contextPath)) {
    return fallbackRedirect(contextPath);
  }
  if (pathname.startsWith('/go/')) {
    return ensureContextPath(pathname, contextPath);
  }
  if (pathname.startsWith('/react/') || pathname.startsWith('/assets/vite/')) {
    return normalizeLegacyShellAlias(pathname, contextPath);
  }
  if (pathname.startsWith('/portal/')) {
    return normalizePortalRedirectPath(pathname, contextPath);
  }
  const normalizedContextPath = normalizeContextPath(contextPath);
  if (normalizedContextPath && (pathname === normalizedContextPath || pathname.startsWith(`${normalizedContextPath}/`))) {
    return isContextRootPath(pathname, contextPath)
      ? fallbackRedirect(contextPath)
      : normalizePortalRedirectPath(pathname, contextPath);
  }
  return null;
};

const normalizeLegacyShellAlias = (pathname: string, contextPath: string): string => {
  if (
    pathname.endsWith('/react/react-home.html')
    || pathname.endsWith('/assets/vite/react-home.html')
    || pathname.endsWith('/react/react-users.html')
    || pathname.endsWith('/assets/vite/react-users.html')
    || pathname.endsWith('/react/react-stub.html')
    || pathname.endsWith('/assets/vite/react-stub.html')
  ) {
    return fallbackRedirect(contextPath);
  }
  return ensureContextPath(pathname, contextPath);
};

const normalizePortalRedirectPath = (pathname: string, contextPath: string): string => {
  if (
    pathname.endsWith('/react/react-home.html')
    || pathname.endsWith('/assets/vite/react-home.html')
    || pathname.endsWith('/react/react-users.html')
    || pathname.endsWith('/assets/vite/react-users.html')
    || pathname.endsWith('/react/react-stub.html')
    || pathname.endsWith('/assets/vite/react-stub.html')
  ) {
    return fallbackRedirect(contextPath);
  }
  return pathname;
};

const isLegacyUsersShellPath = (pathname: string): boolean =>
  pathname.endsWith('/react/react-users.html')
  || pathname.endsWith('/assets/vite/react-users.html');

export const resolveLoginRedirectTarget = (
  contextPath: string,
  search: string = window.location.search,
  pathname: string = window.location.pathname
): string => {
  const fallback = fallbackRedirect(contextPath);
  const params = new URLSearchParams(search || '');
  const rawRedirect = params.get('redirect');

  if (!rawRedirect) {
    return fallback;
  }

  let parsed: URL;
  try {
    parsed = new URL(rawRedirect, window.location.origin);
  } catch (error) {
    return fallback;
  }

  if (!isAllowedAbsoluteHost(parsed)) {
    return fallback;
  }

  const normalizedPath = normalizeRedirectPath(parsed.pathname || '', contextPath);
  if (!normalizedPath || isLoginPath(normalizedPath)) {
    return fallback;
  }

  const searchSuffix = parsed.search || '';
  let hashSuffix = parsed.hash || '';
  let resolvedPath = normalizedPath;
  const legacyUsersAlias = isLegacyUsersShellPath(parsed.pathname || '');

  if (
    resolvedPath.endsWith('/home.html')
    || resolvedPath.endsWith('/react/react-home.html')
    || resolvedPath.endsWith('/assets/vite/react-home.html')
  ) {
    resolvedPath = fallback;
  } else if (
    resolvedPath.endsWith('/react/react-users.html')
    || resolvedPath.endsWith('/assets/vite/react-users.html')
  ) {
    resolvedPath = fallback;
    if (!hashSuffix) {
      hashSuffix = '#/users';
    }
  }

  if (hashSuffix === '#users') {
    hashSuffix = '#/users';
  }
  if (!hashSuffix && legacyUsersAlias) {
    hashSuffix = '#/users';
  }

  return `${resolvedPath}${searchSuffix}${hashSuffix}`;
};
